import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, CircularProgress, Button, Switch } from "@mui/material";
import { useSnackbar } from "notistack";
import { get_school_tutors_config_api, update_school_tutors_config_api } from "../../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, font, accent, warning, easing, inputSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { currencySymbol, regionOf } from "../../../utils/region";

/**
 * What this school actually sells, and what it charges for it.
 *
 * WHY THIS SCREEN HAS TO EXIST
 * Courses came from the region, so every school got its regulator's whole
 * catalogue whether it taught it or not, and every instructor was implicitly
 * qualified for all of it. The data layer to fix that shipped without any way
 * to set it, which means the default — "everything" — was the only reachable
 * state.
 *
 * EMPTY STILL MEANS EVERYTHING
 * A school that never opens this screen offers what its region implies. That is
 * deliberate: it stops this becoming a setup step that silently makes a working
 * school unbookable the day the field ships. The screen says so rather than
 * showing empty checkboxes that read as "approved for nothing".
 */
export default function CoursesAndPricing() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalogue, setCatalogue] = useState([]);
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState({});

  const load = async () => {
    const res = await get_school_tutors_config_api();
    if (res.code === 200) {
      setCatalogue(res.catalogue || []);
      setConfig(res.data);
      const byLicense = {};
      (res.data?.courses || []).forEach((c) => { byLicense[c.license] = c; });
      setRows(byLicense);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const configured = Object.keys(rows).length > 0;
  const symbol = currencySymbol(regionOf(config));

  const setRow = (license, patch) =>
    setRows((r) => ({ ...r, [license]: { license, enabled: true, ...(r[license] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    const courses = catalogue
      .filter((c) => rows[c.license])
      .map((c) => {
        const r = rows[c.license];
        const rates = {};
        // Only send a tier that has a real number. Sending min30: NaN stores
        // null and silently wipes a price someone typed last week.
        ["min30", "min60"].forEach((k) => {
          const v = Number(r.defaultRates?.[k]);
          if (Number.isFinite(v) && v > 0) rates[k] = v;
        });
        return { license: c.license, enabled: r.enabled !== false, defaultRates: rates };
      });

    const res = await update_school_tutors_config_api({ courses });
    if (res.code === 200) {
      enqueueSnackbar("Courses saved", { variant: "success" });
      setConfig(res.data);
    } else {
      enqueueSnackbar(res.message || "Couldn't save courses", { variant: "error" });
    }
    setSaving(false);
  };

  const setRateMode = async (mode) => {
    setSaving(true);
    const res = await update_school_tutors_config_api({ rateMode: mode });
    if (res.code === 200) { setConfig(res.data); enqueueSnackbar("Saved", { variant: "success" }); }
    else enqueueSnackbar(res.message || "Couldn't save", { variant: "error" });
    setSaving(false);
  };

  if (loading) return <CircularProgress size={20} sx={{ color: accent }} />;

  return (
    <Box>
      <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 2, lineHeight: 1.55, maxWidth: 620 }}>
        Turn on the courses your school teaches and set a price for each. Anything left off is not bookable and does not
        appear to students.{" "}
        {!configured && (
          <Box component="span" sx={{ color: warning, fontWeight: 600 }}>
            You have not set this yet, so every course your region offers is currently on.
          </Box>
        )}
      </Typography>

      {catalogue.map((c) => {
        const row = rows[c.license];
        const on = Boolean(row) && row.enabled !== false;
        return (
          <Box
            key={c.license}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
              border: `1px solid ${on ? accent + "44" : border}`,
              backgroundColor: on ? `${accent}06` : "#fff",
              borderRadius: "10px", p: 1.5, mb: 1, transition: `all 0.15s ${easing}`,
            }}
          >
            <Switch
              size="small"
              checked={on}
              onChange={(e) => setRow(c.license, { enabled: e.target.checked })}
              sx={{ "& .Mui-checked": { color: accent }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: accent } }}
            />
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
                {c.label}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>
                {c.fullLabel}
              </Typography>
            </Box>

            <TextField
              size="small" label={`30 min (${symbol})`} type="number" disabled={!on}
              value={row?.defaultRates?.min30 ?? ""}
              onChange={(e) => setRow(c.license, { defaultRates: { ...(row?.defaultRates || {}), min30: e.target.value } })}
              sx={{ ...inputSx, width: 128 }}
            />
            <TextField
              size="small" label={`60 min (${symbol})`} type="number" disabled={!on}
              value={row?.defaultRates?.min60 ?? ""}
              onChange={(e) => setRow(c.license, { defaultRates: { ...(row?.defaultRates || {}), min60: e.target.value } })}
              sx={{ ...inputSx, width: 128 }}
            />
          </Box>
        );
      })}

      <Button onClick={save} disabled={saving} sx={{
        mt: 1, textTransform: "none", fontFamily: font, fontWeight: 700, fontSize: "12.5px",
        color: "#fff", backgroundColor: accent, borderRadius: "999px", px: 2, py: 0.5,
        "&:hover": { backgroundColor: "#a81828" },
      }}>
        {saving ? "Saving…" : "Save courses"}
      </Button>

      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${border}` }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary, mb: 0.5 }}>
          Who sets the price
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1.5, lineHeight: 1.55, maxWidth: 620 }}>
          This is the default. You can override it for individual instructors on the Staff page — Dana can set her own
          while Andy stays on the school rate.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25, maxWidth: 620 }}>
          {[
            { key: "org_standard", title: "The school's price", body: "Every instructor charges what you set above." },
            { key: "instructor_set", title: "Instructors set their own", body: "Each instructor prices their own time; your price is the fallback." },
          ].map((o) => {
            const on = (config?.rateMode || "org_standard") === o.key;
            return (
              <Box
                key={o.key}
                onClick={() => !saving && setRateMode(o.key)}
                sx={{
                  p: 1.5, borderRadius: "10px", cursor: saving ? "default" : "pointer", backgroundColor: "#fff",
                  border: `1.5px solid ${on ? accent : border}`, transition: `all 0.15s ${easing}`,
                }}
              >
                <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: on ? accent : textPrimary, mb: 0.25 }}>
                  {on ? "✓ " : ""}{o.title}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.5 }}>
                  {o.body}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
